import { AwesomeEvent, Value } from '@philips-emr/tws-core';
import { Validatable, ValidateEventListener } from '@philips-emr/tws-core/lib/component/types';
import { validate, Validation, ValidatorFunction } from '@philips-emr/tws-core/lib/services/model-service';

import BaseFieldAPI from './base-field-api';

export interface Validators<T> {
  [key: string]: ValidatorFunction<T>;
}

class FieldAPI extends BaseFieldAPI implements Validatable {

  /** @event */
  @AwesomeEvent
  onValidate = new ValidateEventListener();

  getValue(): Value {
    const data = this.getData();
    const entry = this.getEntry();

    return data[entry];
  }

  setValue(value: Value): void {
    const { validatedOnChange, entry, data } = this.state;

    const oldValue = data[entry];
    data[entry] = value;

    this.setState({ data });

    if (validatedOnChange) {
      this.validate();
    }

    this.onChange.fire({ entry, value, oldValue });
  }

  isValidatedOnChange(): boolean {
    return this.state.validatedOnChange;
  }

  setValidatedOnChange(validatedOnChange: boolean): void {
    this.setState({ validatedOnChange });
  }

  getValidators(): string[] {
    return this.state.validators;
  }

  setValidators(validators: string[]): void {
    this.setState({ validators });

    this.updateValidations();
  }

  addValidator(name: string): void {
    const { validators = [] } = this.state;
    this.setState({ validators: [...validators, name] });

    this.updateValidations();
  }

  removeValidator(name: string): void {
    const { validators = [] } = this.state;
    this.setState({ validators: validators.filter((v: string) => v !== name) });

    this.updateValidations();
  }

  isValid(): boolean {
    const { valid } = this.runValidations();
    return valid;
  }

  validate(): boolean {
    const { entry } = this.state;

    const { validations, valid } = this.runValidations();

    this.setState({ validations, showValidations: true });

    this.onValidate.fire({ entry, validations, valid });

    return valid;
  }

  addLocalValidator(name: string, localValidator: ValidatorFunction<Value>): void {
    const { localValidators } = this.state;

    if (name in localValidators) {
      console.warn(`[Field API]: There is already a validator with the name ${name}, overwriting it now.`);
    }

    this.setState({ localValidators: { ...localValidators, [name]: localValidator } });
  }

  removeLocalValidator(name: string): void {
    const { localValidators: localValidatorsOld } = this.state;

    const localValidators: Validators<Value> = { ...localValidatorsOld };

    delete localValidators[name];

    this.setState({ localValidators });
  }

  private runValidations(): { validations: Validation[]; valid: boolean } {
    const { childValidations = [], validators = [] } = this.state;
    const localValidators: Validators<Value> = this.state.localValidators;

    const value = this.getValue();
    const validationRunner = validate(value);

    validators.forEach((validator: { name: string; options: {} }) => {
      validationRunner.apply(validator.name, validator.options);
    });

    const localValidations: Validation[] = Object.values(localValidators).flatMap((validator: ValidatorFunction<Value>) => validator(value));

    const standardValidations = validationRunner.run();
    const validations = [...localValidations, ...standardValidations, ...childValidations];
    const valid = validations.every(v => v.valid === true);

    return { validations, valid };
  }

  private updateValidations(): void {
    const { validations, valid } = this.runValidations();
    this.setState({ validations, valid });
  }
}

export default FieldAPI;
